import React, { useEffect, useMemo, useState } from 'react';
import {
  Modal, Upload, Button, Table, Tag, Alert, Descriptions, Select, Typography, Space, Input, Steps, Result, Tooltip, Radio, DatePicker, Form, InputNumber, message,
} from 'antd';
import { InboxOutlined, RobotOutlined, CheckCircleOutlined, WarningOutlined, CloseCircleOutlined, PlusOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import dayjs from 'dayjs';
import { supplierOrderAIImportAPI, supplierAPI, productAPI } from '../services/api.ts';
import { generateProductDisplayName } from '../utils/productUtils.ts';
import { useAuth } from '../contexts/AuthContext.tsx';

const { Dragger } = Upload;
const { Text } = Typography;

interface Props { visible: boolean; onClose: () => void; onSuccess: () => void; }

interface Candidate { id: number; internal_product_code: string; size?: string | null; color?: string | null; product_description?: string | null; }
interface Check { code: string; ok: boolean; message: string; }
interface Line {
  row: number; marks: string; description: string; size: string | null; technical_notes: string | null;
  quantity: number | null; unit_price: number | null; total_price: number | null;
  matched: boolean; match_method: string; product_id: number | null; product: Candidate | null; candidates: Candidate[];
  destination: { customer_order_id: number | null; customer_order: { id: number; order_number: string; reorder: boolean } | null; customer: { id: number; company: string } | null; method: string; warning: string | null };
  checks: Check[];
}
interface ImportResult {
  import_id: number; filename: string; document_type: { detected: string; label: string; warning: string | null };
  extraction: any; supplier_match: any; lines: Line[]; checks: Check[]; missing_fields: string[];
  cost: { estimated_cost_usd: number | null; duration_ms: number | null };
}
interface AnalyzeResult {
  imports: ImportResult[];
  supplier: { supplier_id: number | null; supplier_name: string | null; method: string; candidates: { id: number; name: string }[]; extracted_name: string | null };
  existing_orders: { id: number; order_number: string; order_date: string | null; status_name: string | null; customer_order_number: string | null; product_ids: number[] }[];
  open_customer_orders: { id: number; order_number: string; customer_company: string | null; reorder: boolean }[];
  can_create_products: boolean;
  budget: { budget_usd: number | null; spent_usd: number; blocked: boolean; warning: boolean };
}
type Choice = { product_id?: number; skip?: boolean; new_product?: any; customer_order_id?: number | null };

const METHOD_LABEL: Record<string, { text: string; color: string }> = {
  alias: { text: 'corrispondenza', color: 'green' },
  supplier_code: { text: 'codice fornitore', color: 'green' },
  none: { text: 'da scegliere', color: 'orange' },
};

/**
 * Importa con AI (v3.4.0): proforma del fornitore → ordine fornitore nuovo o righe aggiunte a un
 * ordine aperto. 1) file (anche più PI) → 2) anteprima con fornitore, ordine, righe con prodotto
 * (scegli / crea nuovo / salta) e destinazione per riga dalla sigla → 3) esito.
 */
const SupplierOrderAIImportModal: React.FC<Props> = ({ visible, onClose, onSuccess }) => {
  const { canWrite } = useAuth();
  const [step, setStep] = useState(0);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<AnalyzeResult | null>(null);
  const [outcome, setOutcome] = useState<any | null>(null);
  const [existing, setExisting] = useState<{ message: string } | null>(null);

  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [supplierId, setSupplierId] = useState<number | undefined>();
  const [mode, setMode] = useState<'new' | 'append'>('new');
  const [existingOrderId, setExistingOrderId] = useState<number | undefined>();
  const [header, setHeader] = useState<{ order_number: string; order_date: any; order_status_id: number; customer_order_id?: number; stock_code?: string; delivery_date?: any }>({ order_number: '', order_date: dayjs(), order_status_id: 1 });
  const [choices, setChoices] = useState<Record<string, Choice>>({});
  const [newProductFor, setNewProductFor] = useState<{ key: string; line: Line } | null>(null);
  const [npForm] = Form.useForm();

  const key = (imp: ImportResult, l: Line) => `${imp.import_id}:${l.row}`;

  useEffect(() => {
    if (visible) {
      setStep(0); setFileList([]); setResult(null); setOutcome(null); setExisting(null); setChoices({}); setMode('new'); setExistingOrderId(undefined);
      setHeader({ order_number: '', order_date: dayjs(), order_status_id: 1 });
      supplierAPI.getSuppliers().then(setSuppliers).catch(() => setSuppliers([]));
      productAPI.getProducts().then((d: any) => setProducts(Array.isArray(d) ? d : d?.items || [])).catch(() => setProducts([]));
    }
  }, [visible]);

  const handleAnalyze = async (force = false) => {
    const files = fileList.map(f => f.originFileObj as File).filter(Boolean);
    if (!files.length) { message.warning('Selezionare almeno un file'); return; }
    setAnalyzing(true); setExisting(null);
    try {
      const data: AnalyzeResult = await supplierOrderAIImportAPI.analyze(files, force);
      setResult(data);
      setSupplierId(data.supplier.supplier_id || undefined);
      const init: Record<string, Choice> = {};
      data.imports.forEach(imp => imp.lines.forEach(l => {
        init[key(imp, l)] = { product_id: l.product_id || undefined, customer_order_id: l.destination.customer_order_id || undefined };
      }));
      setChoices(init);
      const firstDate = data.imports[0]?.extraction?.pi_date;
      setHeader(h => ({ ...h, order_date: firstDate ? dayjs(firstDate) : dayjs() }));
      setStep(1);
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && detail?.existing_import_id) setExisting({ message: detail.message });
      else message.error((typeof detail === 'string' ? detail : detail?.message) || "Errore durante l'analisi", 8);
    } finally { setAnalyzing(false); }
  };

  const allLines = useMemo(() => (result?.imports || []).flatMap(imp => imp.lines.map(l => ({ imp, l, k: key(imp, l) }))), [result]);
  const pending = allLines.filter(x => { const c = choices[x.k] || {}; return !c.skip && !c.product_id && !c.new_product; });
  const chosen = allLines.filter(x => { const c = choices[x.k] || {}; return !c.skip && (c.product_id || c.new_product); });
  const existingOrder = result?.existing_orders.find(o => o.id === existingOrderId);
  const alreadyInOrder = (c: Choice) => mode === 'append' && !!existingOrder && !!c.product_id && existingOrder.product_ids.includes(c.product_id);
  const blockedRows = allLines.filter(x => alreadyInOrder(choices[x.k] || {}) && !(choices[x.k] || {}).skip);

  const handleConfirm = async () => {
    if (!result || !supplierId) { message.warning('Selezionare il fornitore'); return; }
    if (mode === 'new' && !header.order_number.trim()) { message.warning('Indicare il numero del nuovo ordine fornitore'); return; }
    if (mode === 'append' && !existingOrderId) { message.warning("Selezionare l'ordine a cui aggiungere le righe"); return; }
    if (pending.length) { message.warning(`${pending.length} righe senza scelta: scegli un prodotto, crealo o salta la riga`); return; }
    if (blockedRows.length) { message.warning(`${blockedRows.length} righe hanno un prodotto già presente nell'ordine: saltale`); return; }
    if (!chosen.length) { message.warning('Nessuna riga con prodotto'); return; }
    setConfirming(true);
    try {
      const res = await supplierOrderAIImportAPI.confirm({
        import_ids: result.imports.map(i => i.import_id),
        mode, supplier_id: supplierId, existing_order_id: mode === 'append' ? existingOrderId : undefined,
        order: mode === 'new' ? {
          order_number: header.order_number.trim(), order_date: header.order_date ? header.order_date.format('YYYY-MM-DD') : undefined,
          order_status_id: header.order_status_id, customer_order_id: header.customer_order_id, stock_code: header.stock_code || undefined,
          delivery_date: header.delivery_date ? header.delivery_date.format('YYYY-MM-DD') : undefined,
        } : undefined,
        lines: allLines.map(({ imp, l, k }) => {
          const c = choices[k] || {};
          return { import_id: imp.import_id, row: l.row, product_id: c.new_product ? null : (c.product_id ?? null), new_product: c.new_product || null, skip: !!c.skip, customer_order_id: c.customer_order_id ?? null };
        }),
      });
      setOutcome(res); setStep(2); onSuccess();
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      message.error((typeof detail === 'string' ? detail : detail?.message) || 'Errore durante la creazione', 8);
    } finally { setConfirming(false); }
  };

  const handleDiscard = async () => {
    if (result) { for (const imp of result.imports) { try { await supplierOrderAIImportAPI.discard(imp.import_id); } catch { /* non bloccante */ } } }
    onClose();
  };

  const productOptions = useMemo(() => products.map((p: any) => ({ value: p.id, label: `${generateProductDisplayName(p.internal_product_code, p.size, p.color)} - ${p.product_description || ''}` })), [products]);
  const orderOptions = useMemo(() => (result?.open_customer_orders || []).map(o => ({ value: o.id, label: `${o.order_number}${o.reorder ? ' (stock)' : ''} - ${o.customer_company || ''}` })), [result]);

  const openNewProduct = (k: string, l: Line) => {
    setNewProductFor({ key: k, line: l });
    npForm.setFieldsValue({ internal_product_code: '', product_description: l.description, size: '', color: '', dogana_code: '', cost: l.unit_price ?? undefined, supplier_product_code: '' });
  };
  const saveNewProduct = async () => {
    try {
      const v = await npForm.validateFields();
      if (newProductFor) setChoices(prev => ({ ...prev, [newProductFor.key]: { ...prev[newProductFor.key], product_id: undefined, skip: false, new_product: v } }));
      setNewProductFor(null);
    } catch { /* validazione */ }
  };

  const columns = [
    { title: 'PI', width: 110, render: (x: any) => <Text style={{ fontSize: 12 }}>{x.imp.extraction?.pi_number || x.imp.filename}</Text> },
    { title: 'Sigla', dataIndex: ['l', 'marks'], width: 70, render: (v: string) => v ? <Tag>{v}</Tag> : '-' },
    {
      title: 'Ordine cliente (destinazione riga)', width: 260, render: (x: any) => {
        const c = choices[x.k] || {}; const d = x.l.destination;
        return (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Select allowClear showSearch optionFilterProp="label" style={{ width: '100%' }} placeholder={header.customer_order_id ? 'come testata' : 'nessuna'}
              value={c.customer_order_id ?? undefined} onChange={(v) => setChoices(prev => ({ ...prev, [x.k]: { ...prev[x.k], customer_order_id: v ?? null } }))} options={orderOptions} />
            {d.method === 'mark' && c.customer_order_id === d.customer_order_id && d.customer_order_id && <Text type="secondary" style={{ fontSize: 11 }}>dalla sigla {x.l.marks}</Text>}
            {d.warning && <Text type="warning" style={{ fontSize: 11 }}>{d.warning}</Text>}
          </Space>
        );
      },
    },
    {
      title: 'Riga del PI', render: (x: any) => (
        <div>{x.l.description}{x.l.size ? <Text type="secondary"> · {x.l.size}</Text> : null}
          {x.l.technical_notes && <div><Text type="secondary" style={{ fontSize: 11 }}>{x.l.technical_notes.slice(0, 120)}</Text></div>}</div>
      ),
    },
    { title: 'Q.tà', width: 70, align: 'right' as const, render: (x: any) => x.l.quantity ?? '-' },
    { title: 'Prezzo', width: 90, align: 'right' as const, render: (x: any) => x.l.unit_price != null ? x.l.unit_price.toFixed(4) : '-' },
    {
      title: 'Totale', width: 100, align: 'right' as const, render: (x: any) => {
        const bad = x.l.checks.filter((c: Check) => !c.ok);
        return <span>{x.l.total_price != null ? x.l.total_price.toFixed(2) : '-'} {bad.length > 0 && <Tooltip title={bad.map((c: Check) => c.message).join(' · ')}><WarningOutlined style={{ color: '#fa8c16' }} /></Tooltip>}</span>;
      },
    },
    {
      title: 'Prodotto NAVI', width: 400, render: (x: any) => {
        const c = choices[x.k] || {}; const l: Line = x.l;
        const proposed = METHOD_LABEL[l.match_method] || METHOD_LABEL.none;
        const opts = l.candidates.length && !l.matched
          ? [{ label: 'Candidati proposti', options: l.candidates.map(cd => ({ value: cd.id, label: `${generateProductDisplayName(cd.internal_product_code, cd.size, cd.color)} - ${cd.product_description || ''}` })) }, { label: 'Tutti i prodotti', options: productOptions }]
          : productOptions;
        const blocked = alreadyInOrder(c) && !c.skip;
        return (
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Space size={4} wrap>
              {c.skip ? <Tag icon={<CloseCircleOutlined />}>saltata</Tag>
                : c.new_product ? <Tag icon={<PlusOutlined />} color="blue">nuovo prodotto {c.new_product.internal_product_code}</Tag>
                : l.matched && c.product_id === l.product_id ? <Tag icon={<CheckCircleOutlined />} color={proposed.color}>{proposed.text}</Tag>
                : c.product_id ? <Tag color="blue">scelto a mano</Tag>
                : <Tag icon={<WarningOutlined />} color={l.candidates.length ? 'orange' : 'red'}>{l.candidates.length ? 'da confermare' : 'non trovato'}</Tag>}
              {blocked && <Tag color="red">già nell'ordine</Tag>}
            </Space>
            <Space.Compact style={{ width: '100%' }}>
              <Select style={{ width: '100%' }} showSearch optionFilterProp="label" placeholder="Scegli il prodotto…" disabled={!!c.skip || !!c.new_product}
                value={c.new_product ? undefined : c.product_id} onChange={(v) => setChoices(prev => ({ ...prev, [x.k]: { ...prev[x.k], product_id: v, new_product: undefined } }))} options={opts as any} />
              {result?.can_create_products && !c.skip && (
                <Tooltip title="Crea un nuovo prodotto in anagrafica"><Button icon={<PlusOutlined />} onClick={() => openNewProduct(x.k, l)} /></Tooltip>
              )}
              <Button onClick={() => setChoices(prev => ({ ...prev, [x.k]: { ...prev[x.k], skip: !prev[x.k]?.skip } }))}>{c.skip ? 'Ripristina' : 'Salta'}</Button>
            </Space.Compact>
          </Space>
        );
      },
    },
  ];

  const supMethod = result?.supplier.method;
  const supNote = supMethod === 'alias' || supMethod === 'exact' ? null : supMethod === 'tokens' ? 'fornitore proposto per somiglianza del nome: verifica' : 'fornitore non riconosciuto: selezionalo';

  return (
    <Modal open={visible} title={<span><RobotOutlined /> Importa con AI (proforma del fornitore - PI)</span>} width={1250}
      onCancel={step === 2 ? onClose : handleDiscard} maskClosable={false} className="no-uppercase-modal"
      footer={step === 0 ? [
        <Button key="c" onClick={onClose}>Annulla</Button>,
        <Button key="a" type="primary" icon={<RobotOutlined />} loading={analyzing} disabled={!fileList.length} onClick={() => handleAnalyze(false)}>Analizza con AI</Button>,
      ] : step === 1 ? [
        <Button key="b" onClick={() => setStep(0)} disabled={confirming}>Indietro</Button>,
        <Button key="d" onClick={handleDiscard} disabled={confirming}>Scarta</Button>,
        <Button key="ok" type="primary" icon={<CheckCircleOutlined />} loading={confirming} onClick={handleConfirm}
          disabled={!supplierId || pending.length > 0 || blockedRows.length > 0 || chosen.length === 0 || (mode === 'new' ? !header.order_number.trim() : !existingOrderId)}>
          {mode === 'new' ? 'Crea ordine fornitore' : "Aggiungi righe all'ordine"}
        </Button>,
      ] : [<Button key="x" type="primary" onClick={onClose}>Chiudi</Button>]}
    >
      <Steps size="small" current={step} style={{ marginBottom: 16 }} items={[{ title: 'File' }, { title: 'Anteprima e correzioni' }, { title: 'Esito' }]} />

      {step === 0 && (
        <div>
          <Dragger accept=".pdf,.doc,.docx,.xls,.xlsx,.ods,.png,.jpg,.jpeg" multiple fileList={fileList} beforeUpload={() => false}
            onChange={({ fileList: fl }) => { setFileList(fl.slice(0, 20)); setExisting(null); }} onRemove={(f) => { setFileList(fileList.filter(x => x.uid !== f.uid)); return true; }}>
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Trascina qui una o più proforma (PI) del fornitore</p>
            <p className="ant-upload-hint">PDF, Word, Excel, ODS o immagine; massimo 10 MB e 30 pagine per file, 20 file. Più PI dello stesso fornitore diventano un solo ordine.</p>
          </Dragger>
          {existing && <Alert type="warning" showIcon style={{ marginTop: 12 }} message={existing.message} action={<Button size="small" type="primary" loading={analyzing} onClick={() => handleAnalyze(true)}>Rianalizza comunque</Button>} />}
        </div>
      )}

      {step === 1 && result && (
        <div>
          {result.imports.filter(i => i.document_type.warning).map(i => <Alert key={i.import_id} type="warning" showIcon style={{ marginBottom: 8 }} message={`${i.filename}: ${i.document_type.warning}`} />)}
          {result.budget?.warning && <Alert type="warning" showIcon style={{ marginBottom: 8 }} message="Superato l'80% del budget mensile AI." />}
          {result.imports.flatMap(i => i.checks.filter(c => !c.ok).map((c, k) => <Alert key={`${i.import_id}-${k}`} type="warning" showIcon style={{ marginBottom: 8 }} message={`${i.extraction?.pi_number || i.filename}: ${c.message}`} />))}

          <Descriptions size="small" bordered column={2} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="PI analizzati" span={2}>
              {result.imports.map(i => <Tag key={i.import_id}>{i.extraction?.pi_number || i.filename}{i.extraction?.pi_date ? ` · ${i.extraction.pi_date}` : ''}{i.cost?.estimated_cost_usd != null ? ` · ${i.cost.estimated_cost_usd.toFixed(3)} $` : ''}</Tag>)}
            </Descriptions.Item>
            <Descriptions.Item label="Fornitore" span={2}>
              <Space direction="vertical" size={2}>
                {result.supplier.extracted_name && <Text type="secondary">nel documento: {result.supplier.extracted_name}</Text>}
                <Select style={{ width: 420 }} showSearch optionFilterProp="label" placeholder="Seleziona il fornitore" value={supplierId} onChange={setSupplierId}
                  options={suppliers.map((s: any) => ({ value: s.id, label: s.name }))} />
                {supNote && <Text type={supMethod === 'none' ? 'danger' : 'warning'}>{supNote}</Text>}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Destinazione" span={2}>
              <Radio.Group value={mode} onChange={e => setMode(e.target.value)}>
                <Radio value="new">Nuovo ordine fornitore</Radio>
                <Radio value="append" disabled={!result.existing_orders.length}>Aggiungi righe a un ordine aperto ({result.existing_orders.length})</Radio>
              </Radio.Group>
            </Descriptions.Item>
            {mode === 'new' ? (
              <>
                <Descriptions.Item label="Numero ordine *"><Input value={header.order_number} onChange={e => setHeader({ ...header, order_number: e.target.value.toUpperCase() })} placeholder="Numero del nuovo ordine" style={{ width: 240 }} /></Descriptions.Item>
                <Descriptions.Item label="Data ordine"><DatePicker format="DD/MM/YYYY" value={header.order_date} onChange={(v) => setHeader({ ...header, order_date: v })} /></Descriptions.Item>
                <Descriptions.Item label="Stato"><Tag>Nuovo</Tag> <Text type="secondary">l'ordine nasce in Nuovo: si modifica e si avanza poi con Modifica / Cambia Stato</Text></Descriptions.Item>
                <Descriptions.Item label="Stock code"><Input value={header.stock_code || ''} onChange={e => setHeader({ ...header, stock_code: e.target.value.toUpperCase() })} placeholder="opzionale" style={{ width: 200 }} /></Descriptions.Item>
                <Descriptions.Item label="Ordine cliente (valore predefinito righe)" span={2}>
                  <Select allowClear showSearch optionFilterProp="label" style={{ width: 420 }} value={header.customer_order_id} onChange={(v) => setHeader({ ...header, customer_order_id: v })} options={orderOptions} placeholder="nessuno" />
                </Descriptions.Item>
              </>
            ) : (
              <Descriptions.Item label="Ordine esistente *" span={2}>
                <Select style={{ width: 520 }} showSearch optionFilterProp="label" value={existingOrderId} onChange={setExistingOrderId} placeholder="Ordini aperti del fornitore senza stock caricato"
                  options={result.existing_orders.map(o => ({ value: o.id, label: `${o.order_number} · ${o.status_name || ''}${o.customer_order_number ? ` · ${o.customer_order_number}` : ''} · ${o.product_ids.length} righe` }))} />
                {existingOrder && blockedRows.length > 0 && <div><Text type="danger">{blockedRows.length} righe hanno un prodotto già presente nell'ordine: vanno saltate</Text></div>}
              </Descriptions.Item>
            )}
          </Descriptions>

          <Table size="small" rowKey="k" columns={columns} dataSource={allLines} pagination={false} scroll={{ y: 380 }} />
          <div style={{ marginTop: 8 }}><Text type="secondary">{chosen.length} righe con prodotto · {allLines.filter(x => choices[x.k]?.skip).length} saltate · {pending.length} da decidere{!result.can_create_products ? ' · (non hai il permesso di creare prodotti)' : ''}</Text></div>

          <Modal open={!!newProductFor} title="Nuovo prodotto in anagrafica" onOk={saveNewProduct} onCancel={() => setNewProductFor(null)} okText="Usa questo prodotto" cancelText="Annulla">
            <Form form={npForm} layout="vertical">
              <Form.Item name="internal_product_code" label="Codice interno" rules={[{ required: true, message: 'Obbligatorio' }]}><Input style={{ textTransform: 'uppercase' }} /></Form.Item>
              <Form.Item name="product_description" label="Descrizione" rules={[{ required: true, message: 'Obbligatoria' }]}><Input /></Form.Item>
              <Space>
                <Form.Item name="size" label="Taglia"><Input style={{ width: 120, textTransform: 'uppercase' }} /></Form.Item>
                <Form.Item name="color" label="Colore"><Input style={{ width: 120, textTransform: 'uppercase' }} /></Form.Item>
                <Form.Item name="dogana_code" label="Codice doganale"><Input style={{ width: 160 }} /></Form.Item>
              </Space>
              <Space>
                <Form.Item name="cost" label="Costo"><InputNumber min={0} precision={4} /></Form.Item>
                <Form.Item name="supplier_product_code" label="Codice del fornitore"><Input /></Form.Item>
              </Space>
              <Text type="secondary">Il prodotto viene creato alla conferma dell'ordine, associato a questo fornitore.</Text>
            </Form>
          </Modal>
        </div>
      )}

      {step === 2 && outcome && (
        <Result status="success" title={outcome.mode === 'new' ? `Ordine fornitore ${outcome.order_number} creato` : `Righe aggiunte all'ordine ${outcome.order_number}`}
          subTitle={`${outcome.supplier} · ${outcome.details_created} righe inserite${outcome.lines_skipped ? ` · ${outcome.lines_skipped} saltate (vedi note)` : ''}${outcome.products_created?.length ? ` · prodotti creati: ${outcome.products_created.join(', ')}` : ''}`}
          extra={outcome.learned && (outcome.learned.products || outcome.learned.supplier || outcome.learned.marks) ? (
            <Alert type="info" showIcon message={`NAVI ha memorizzato: ${[outcome.learned.supplier ? 'fornitore' : null, outcome.learned.products ? `${outcome.learned.products} prodotti` : null, outcome.learned.marks ? `${outcome.learned.marks} sigle` : null].filter(Boolean).join(', ')}. La prossima volta saranno abbinati da soli.`} />
          ) : null} />
      )}
    </Modal>
  );
};

export default SupplierOrderAIImportModal;
